import React, { useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { Link, Navigate } from "react-router-dom";

import images from "../../utils/constant/images";
import routerPath from "../../utils/router/routerPath";
import { isApexHost } from "../../utils/sessionBridge";
import { BULLETS, GROUPS, HERO, PREVIEW, STEPS } from "./features";
import "./style.css";

function matches(feature, group, query) {
  if (!query) return true;
  return `${feature[0]} ${feature[1]} ${group.title}`.toLowerCase().includes(query);
}

const LandingPage = () => {
  const sessionUser = useSelector((state) => state.session.user);
  const signedIn = Boolean(sessionUser && sessionUser.refresh);
  const [query, setQuery] = useState("");
  const needle = query.trim().toLowerCase();
  const visible = useMemo(() => GROUPS.map((group) => ({
    ...group,
    features: group.features.filter((feature) => matches(feature, group, needle)),
  })).filter((group) => group.features.length), [needle]);
  const shown = visible.reduce((sum, group) => sum + group.features.length, 0);

  if (signedIn) return <Navigate to={routerPath.home} replace />;
  if (!isApexHost()) return <Navigate to={routerPath.login} replace />;

  return (
    <div className="lw-front">
      <Helmet>
        <title>Letswebinar — host the session</title>
        <meta
          name="description"
          content="Letswebinar schedules the event, lets guests join from a link, and keeps chat, questions, reminders and attendance with that webinar."
        />
      </Helmet>
      <header className="lw-bar">
        <div className="lw-wrap lw-bar__in">
          <a className="lw-brand" href="#top">
            <img src={images.logo} alt="" />
            <span>Letswebinar</span>
          </a>
          <nav className="lw-nav" aria-label="Primary">
            <a className="lw-quiet" href="#features">Features</a>
            <Link className="lw-btn" to={routerPath.login}>Log in</Link>
          </nav>
        </div>
      </header>
      <main id="top">
        <div className="lw-wrap lw-hero">
          <div>
            <p className="lw-kicker">{HERO.eyebrow}</p>
            <h1>{HERO.headline} <span>{HERO.accent}</span></h1>
            <p className="lw-lead">{HERO.lead}</p>
            <ul className="lw-ticks">
              {BULLETS.map((item) => <li key={item}>{item}</li>)}
            </ul>
          </div>
          <aside className="lw-stage" aria-label={PREVIEW.title}>
            <div className="lw-stage__screen">
              <span className="lw-stage__play" aria-hidden="true" />
              <p>{PREVIEW.title}</p>
            </div>
            {PREVIEW.rows.map(([label, value]) => (
              <div className="lw-stage__row" key={label}>
                <span>{label}</span>
                <b>{value}</b>
              </div>
            ))}
          </aside>
        </div>
        <div className="lw-wrap" id="features">
          <div className="lw-tools">
            <nav className="lw-jumps" aria-label="Feature groups">
              {GROUPS.map((group) => (
                <a key={group.id} href={`#${group.id}`}>{group.title}</a>
              ))}
            </nav>
            <input
              className="lw-filter"
              type="search"
              value={query}
              placeholder="Find a feature"
              aria-label="Find a feature"
              onChange={(event) => setQuery(event.target.value)}
            />
            <span className="lw-count" aria-live="polite">
              {needle ? `${shown} matching` : ""}
            </span>
          </div>
          {shown === 0 ? <p className="lw-empty">No features match that search.</p> : null}
          {visible.map((group) => (
            <section className="lw-group" id={group.id} key={group.id}>
              <h2>{group.title}</h2>
              <p>{group.intro}</p>
              <div className="lw-grid">
                {group.features.map((feature) => (
                  <article className="lw-card" key={feature[0]}>
                    <h3>{feature[0]}</h3>
                    <p>{feature[1]}</p>
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>
        <div className="lw-wrap">
          <h2>How it starts</h2>
          <div className="lw-steps">
            {STEPS.map(([title, text], index) => (
              <div className="lw-step" key={title}>
                <span>{index + 1}</span>
                <h3>{title}</h3>
                <p>{text}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="lw-wrap lw-open">
          <h2>Log in</h2>
          <p>Each organisation opens on its own Letswebinar hostname. On the login page, enter the organisation code. Apple is an example of the shape.</p>
          <Link className="lw-btn" to={routerPath.login}>Log in</Link>
        </div>
      </main>
      <footer className="lw-foot">
        <div className="lw-wrap">
          <p>© {new Date().getFullYear()} Letswebinar.</p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
